import { isFeatureEnabled } from '@sdk/utils/isFeatureEnabled';
import { TimesheetStatus } from '@sdk/timesheets/api/apiTypes';

import { TIMESHEETS_TABLE_COLUMN_HEADERS } from '@web/timeClock/constants';
import { getLastEditedByName } from '@web/timeClock/utils/getLastEditedByName';

import config from '@angular/config';
import ReportsTableBaseCtrl from '@angular/reports/ReportsTableBaseCtrl';
import timesheetsTableComponentUrl from '@angular/timeclock/components/timesheetsTableComponent.html';
import { USERS_LIST_GROUPS_COLLAPSE_THRESHOLD } from '@angular/dashboard/constants';

const GROUP_HEADER_ROW_HEIGHT = 41;
const EVENT_ROW_DEFAULT_HEIGHT = 60;
class TimesheetsTableCtrl extends ReportsTableBaseCtrl {
  tableHeaderLabels = TIMESHEETS_TABLE_COLUMN_HEADERS;
  TIME_CARD_NOTES_ENABLED = config.TIME_CARD_NOTES_ENABLED;
  TimesheetStatus = TimesheetStatus;

  sortMethods = {
    date: 'dtstart',
    employee: 'user.fullname',
    fullLegalName: 'user.fullLegalName',
    employeeId: 'user.employeeId',
    location: 'location.name',
    position: 'position.name',
    tags: 'tags[0].name',
    clockedIn: 'clockInTime',
    clockedOut: 'clockOutTime',
    break: 'break',
    actual: 'actual',
    scheduled: 'duration',
    difference: 'difference',
    dtstart: 'scheduledStartTime',
    dtend: 'scheduledEndTime',
    status: 'timesheetStatus',
    source: 'fromIntegration',
    breakStart: 'breakStartArray_sortKey',
    breakEnd: 'breakEndArray_sortKey',
    scheduledBreakDuration: 'breakDurationArray_sortKey',
  };

  constructor(
    Authentication,
    AlertService,
    ConfirmService,
    PersonaCollection,
    EventCollection,
    TimesheetsService,
    TimesheetsUtils,
    Utils,
    IntegrationsEducation,
    sdk,
    extendShiftModel,
    filterFilter,
    toDateFilter,
    toTimeFilter,
    uppercaseFirstFilter,
    $uibModal,
    $parse,
    $scope,
    $rootScope,
    $injector
  ) {
    'ngInject';

    super($injector);
    Object.assign(this, {
      Authentication,
      AlertService,
      ConfirmService,
      PersonaCollection,
      EventCollection,
      TimesheetsService,
      TimesheetsUtils,
      Utils,
      IntegrationsEducation,
      sdk,
      extendShiftModel,
      filterFilter,
      toDateFilter,
      toTimeFilter,
      uppercaseFirstFilter,
      $uibModal,
      $parse,
      $scope,
      $rootScope,
    });
    this.getShiftDurationMinutes = sdk('model-extensions/getShiftDurationMinutes');
    this.AUTOMATED_BREAKS_ENABLED = isFeatureEnabled(
      config.AUTOMATED_BREAKS_ENABLED,
      Authentication.user.org.id
    );
  }

  $onInit() {
    this.columnsSchema = this.schema.items.reduce(
      (agg, item) => ((agg[item.property] = item), agg),
      {}
    );
    this.isTimesheetPreviewActive = false;
    this.isTimesheetEditActive = false;
    this.isEmployeePendingConsentTimesheetPreviewActive = false;

    if (!this.sortMethod) {
      this.sortMethod = 'dtstart';
    }

    this._extendExportDictionary();

    // hardbind and bind out
    this.getDataForExport = this._getDataForExport.bind(this);
    this.toggleCollapse = this._toggleCollapse.bind(this);

    setTimeout(
      () =>
        this.IntegrationsEducation.showStep(
          'toast',
          this.IntegrationsEducation.stepTypes.timesheets
        ),
      1000
    );
  }

  $onChanges(changes) {
    const REFRESH_TRIGGERS = ['data', 'type', 'reportColumns'];
    const changedProperties = Object.keys(changes);
    if (changedProperties.some((prop) => REFRESH_TRIGGERS.includes(prop)) && this.data) {
      this._setItemsHeight();
      this.refreshItems();
      this.getTotals();
    }

    if (changes.bannerShown) {
      this.$rootScope.$broadcast('vsRepeatTrigger');
    }
  }

  // override parent class method for adding a notes column
  _setColumnsForExport() {
    super._setColumnsForExport();
    this._columnsForExport.push({ property: 'assigneeNotes' });
  }

  _extendExportDictionary() {
    Object.assign(this.exportCustomPropertiesDict, {
      status: (event) => {
        if (event.isPastDtendWithNoClockIn) {
          return '-';
        }

        return !event.isGroupHeader ? this.uppercaseFirstFilter(event.timesheetStatus) : '';
      },
      tags: (event) => (event.isGroupHeader ? '' : event.tagsLabel || '-'),
      clockInDistanceFromLocation: (event) => {
        if (event.isPastDtendWithNoClockIn) {
          return '-';
        }

        return event.isGroupHeader
          ? ''
          : event.timesheetProjections
              .map((p) => this._distanceFromLocation(p.clockInDistanceFromLocation))
              .join('\n');
      },
      clockOutDistanceFromLocation: (event) => {
        if (event.isPastDtendWithNoClockIn) {
          return '-';
        }

        return event.isGroupHeader
          ? ''
          : event.timesheetProjections
              .map((p) => this._distanceFromLocation(p.clockOutDistanceFromLocation))
              .join('\n');
      },
      assigneeNotes: (event) => event.assigneeNotes,
    });
  }

  _extendEvent = (event) => {
    const cost = {};
    this.schema.items.forEach((schemaItem) => {
      cost[schemaItem.property] = this.Utils.evaluateExpression(schemaItem.formula, {
        data: event,
        options: {
          showDurationsAsDecimals: this.showDurationsAsDecimals,
        },
      });
    });

    return { ...event, ...cost };
  };

  _addPropsToEditedEvents(data) {
    data.forEach((d) => {
      if (d.timesheetProjections.length) {
        d.lastEditedByName = getLastEditedByName(d, this.PersonaCollection.personasDict);
        d.edited = d.lastEditedByName !== null;
      }
    });
  }

  refreshItems() {
    this.items = this.data?.map(this._extendEvent);
    this.aggregatedEvents = [];
    if (this.items?.length) {
      this._addPropsToEditedEvents(this.items);
      this.aggregatedEvents = this.items.reduce(this._groupEvents, []);
      this._updateCollapsedGroups();
    }
  }

  _groupEvents = (rows, event) => {
    const keys = this.getGroupObjectKeys(event, { allowGroupingByUsers: true });
    keys.forEach((key) => {
      const groupHeaderIndex = this._getGroupIndex(rows, key, this.type);
      rows.push({
        ...event,
        key: `${key.value}_${event.id}`,
        groupKey: key.value,
        keySort: key.sort,
      });

      if (groupHeaderIndex !== null) {
        rows[groupHeaderIndex].totals = this.addToGroupTotals(rows[groupHeaderIndex].totals, event);
      }
    });

    return rows;
  };

  _getEmptyTotals() {
    const totals = {};
    this.schema.items
      .filter(({ sumUp }) => sumUp)
      .forEach(({ property }) => {
        totals[property] = 0;
      });

    return totals;
  }

  addToGroupTotals(totals, event) {
    this.schema.items
      .filter(({ sumUp }) => sumUp)
      .forEach(({ property }) => {
        totals[property] += event[property];
      });

    return totals;
  }

  getEventRowSize(event) {
    if (event.isGroupHeader) {
      return GROUP_HEADER_ROW_HEIGHT;
    }

    if (this.collapsedGroups.includes(event.groupKey)) {
      return 0;
    }

    if (!('$$height' in event)) {
      event.$$height = this.getEventHeight(event);
    }

    return event.$$height || EVENT_ROW_DEFAULT_HEIGHT;
  }

  _getTagsCellHeight(event) {
    if (!event.tags?.length) {
      return 0;
    }

    const isExtendingTagsActive = event.tags.length > USERS_LIST_GROUPS_COLLAPSE_THRESHOLD;
    const buttonHeight = isExtendingTagsActive ? this.COLLAPSE_BUTTON_ROW_HEIGHT : 0;
    let tagsCount;
    if (!isExtendingTagsActive) {
      tagsCount = event.tags.length;
    } else {
      tagsCount =
        !('$$tagsExpanded' in event) || !event.$$tagsExpanded
          ? USERS_LIST_GROUPS_COLLAPSE_THRESHOLD
          : event.tags.length;
    }

    return (
      tagsCount * this.ROW_INNER_TEXT_LINE_HEIGHT +
      buttonHeight +
      this.ROW_PADDING_VERTICAL +
      this.ROW_BORDER_HEIGHT
    );
  }

  _getClockInCellHeight(event) {
    const isActiveModifier = this.reportColumns.reduce((dict, column) => {
      dict[column.property] = column.active && this.canDataColumnBeVisible(column.property) ? 1 : 0;
      return dict;
    }, {});

    return (
      Math.max(
        event.clockedInArray.length * isActiveModifier['clockedIn'],
        event.clockedOutArray.length * isActiveModifier['clockedOut'],
        event.clockedOutArray.length * isActiveModifier['break'],
        event.actualDurationArray.length * isActiveModifier['actual'],
        event.breakStartArray.length * isActiveModifier['breakStart'],
        event.breakEndArray.length * isActiveModifier['breakEnd'],
        event.breakTypeArray.length * isActiveModifier['breakType']
      ) *
        this.ROW_INNER_TEXT_LINE_HEIGHT +
      this.ROW_PADDING_VERTICAL +
      this.ROW_BORDER_HEIGHT
    );
  }

  _getScheduledBreakCellHeight(event) {
    const scheduledMealBreak = this.reportColumns.find((c) => c.property === 'scheduledMealBreak');
    const scheduledRestBreak = this.reportColumns.find((c) => c.property === 'scheduledRestBreak');

    const rows = Math.max(
      (scheduledMealBreak?.active && event.scheduledMealBreakDurationArray?.length) || 0,
      (scheduledRestBreak?.active && event.scheduledRestBreakDurationArray?.length) || 0
    );

    if (!rows) {
      return 0;
    }

    return (
      rows * this.ROW_INNER_TEXT_LINE_HEIGHT + this.ROW_PADDING_VERTICAL + this.ROW_BORDER_HEIGHT
    );
  }

  getEventHeight(event) {
    const tagsCellHeight = this._getTagsCellHeight(event);
    const clockInCellHeight = this._getClockInCellHeight(event);
    const groupsCellHeight = this._getGroupsCellHeight(event.user, event, this.reportColumns);
    const scheduledBreakCellHeight = this._getScheduledBreakCellHeight(event);
    return Math.max(
      this.ROW_MIN_HEIGHT,
      tagsCellHeight,
      clockInCellHeight,
      groupsCellHeight,
      scheduledBreakCellHeight
    );
  }

  get clockInEnabled() {
    return this.Authentication.user.orgSettings.clockInEnabled;
  }

  _setItemsHeight() {
    this.data.forEach((event) => {
      event.$$height = this.getEventHeight(event);
    });
    this.$rootScope.$broadcast('vsRepeatTrigger');
  }

  refreshCellHeight(event) {
    event.$$height = this.getEventHeight(event);
    this.$rootScope.$broadcast('vsRepeatTrigger');
  }

  getSortMethod(columnId) {
    return this.sortMethods[columnId];
  }

  getTotals() {
    this.totals = this._getEmptyTotals();

    this.items.forEach((event) => {
      this.totals = this.addToGroupTotals(this.totals, event);
    });
  }

  shouldShowEditOptions(event, isAdmin) {
    if (event.timesheetStatus === TimesheetStatus.approved && !isAdmin) {
      return false;
    }
    return this.TimesheetsUtils.canEditTimesheet(event);
  }

  canEditTimesheet(event) {
    if (event.timesheetStatus === TimesheetStatus.approved) {
      return false;
    }
    return this.TimesheetsUtils.canEditTimesheet(event);
  }

  canUpdateTimesheetStatus(event, toStatus, isAdmin) {
    if (
      !__HEADLESS__ &&
      this.Authentication.user.canActOnLocation(event.location) &&
      event.timesheetStatus !== toStatus &&
      event.timesheetProjections?.[event.timesheetProjections.length - 1]?.clockOut &&
      toStatus !== TimesheetStatus.pendingConsent
    ) {
      if (!isAdmin && event.timesheetStatus === TimesheetStatus.approved) {
        return false;
      }

      return true;
    }

    return false;
  }

  async viewTimesheet(event, mode = 'edit', _readonly = false, editNoteModeOnly) {
    try {
      const updatedEvent = await this.TimesheetsService.viewTimesheet({
        event,
        mode,
        _readonly,
        editNoteModeOnly,
      });

      this._updateTimesheetOnList(updatedEvent);
    } catch (e) {
      console.warn(e);
      throw e;
    }
  }

  canDeleteTimesheet(event) {
    return event.timesheetStatus !== TimesheetStatus.approved;
  }

  async deleteTimesheet(event) {
    await this.ConfirmService.ask({
      question: 'Are you sure you want to delete this timesheet?',
      confirm: 'Delete',
    });

    const payload = event.timesheetEntries.map((t) => ({ id: t.id, deleted: true }));
    await this.TimesheetsService.bulkUpdate(payload);

    event.timesheetProjections.length = 0; // this clears the array without breaking the reference
    const updatedEvent = this.TimesheetsService.extendTimesheetModel(event);
    updatedEvent.timesheetEntries = [];
    this.refreshCellHeight(updatedEvent);
    this._updateTimesheetOnList(updatedEvent);
  }

  _updateTimesheetOnList(updatedEvent) {
    const index = this.data.findIndex((e) => updatedEvent.id === e.id);
    this.Utils.clearObject(this.data[index]);
    Object.assign(this.data[index], updatedEvent);

    this.refreshItems();
    this.getTotals();
  }

  async markTimesheetAsApproved(event) {
    await this.TimesheetsService.approveTimesheets(
      event.timesheetProjections.map((p) => p.id),
      [event]
    );

    this.onTimesheetSave(event.id, event.user.id);
  }

  async markTimesheetAsPending(event) {
    await this.TimesheetsService.markTimesheetsAsPending(
      event.timesheetProjections.map((p) => p.id),
      [event]
    );

    this.onTimesheetSave(event.id, event.user.id);
  }

  _getUserRow(row) {
    return [row];
  }

  _distanceFromLocation(_distance) {
    let distance = _distance;
    if (!distance) {
      return '';
    }

    const { imperialUnits } = this.Authentication.user.orgSettings;

    if (imperialUnits) {
      distance /= this.$rootScope.FEET_IN_METERS;
    }

    return `${this.Utils.roundNumber(distance, 0)} ${imperialUnits ? 'feet' : 'meters'}`;
  }

  _mapTotalsField(item, field, index) {
    if (index === 0) {
      return 'TOTAL';
    }

    const map = {
      break: () =>
        this.mapMinutesCell(item.break, {
          showDurationsAsDecimals: this.showDurationsAsDecimals,
          displayEmptyWhenZero: true,
        }),
      actual: () =>
        this.mapMinutesCell(item.actual, {
          showDurationsAsDecimals: this.showDurationsAsDecimals,
          displayEmptyWhenZero: true,
        }),
      scheduled: () =>
        this.mapMinutesCell(item.scheduled, {
          showDurationsAsDecimals: this.showDurationsAsDecimals,
        }),
      difference: () =>
        this.mapMinutesCell(item.difference, {
          showDurationsAsDecimals: this.showDurationsAsDecimals,
          delta: true,
          displayEmptyWhenZero: true,
        }),
    };

    return field in map ? map[field]() : '';
  }

  getTableRowClass(row) {
    if (row.isGroupHeader) {
      return this.getGroupHeaderClass(row);
    }

    return this.selectedRows[row.id] ? 'selected' : '';
  }

  get hoveredEvent() {
    return this._hoveredEvent;
  }

  set hoveredEvent(id) {
    this._hoveredEvent = id;
  }

  viewTimeclockNote(event) {
    this.TimesheetsService.openTimeclockNoteModal(
      event.assigneeNotes,
      event.date,
      event.user.fullname
    );
  }

  async viewTimesheetManagerMode(event) {
    if (!this.TIME_CARD_NOTES_ENABLED || !event.id || !this.Authentication.user.isManager) {
      return;
    }

    try {
      const updatedEvent = await this.TimesheetsService.viewTimesheetManagerMode(event);
      this._updateTimesheetOnList(updatedEvent);
    } catch {
      //
    }
  }

  // React refactored methods
  openTimesheetPreview(shift) {
    this.isTimesheetPreviewActive = true;
    this.timesheetPreviewShift = shift;
  }

  closeTimesheetPreview() {
    this.isTimesheetPreviewActive = false;
    this.timesheetPreviewShift = null;
  }

  openEmployeePendingConsentTimesheetPreview(shift) {
    this.isEmployeePendingConsentTimesheetPreviewActive = true;
    this.employeePendingConsentTimesheetPreviewShift = shift;
  }

  closeEmployeePendingConsentTimesheetPreview() {
    this.isEmployeePendingConsentTimesheetPreviewActive = false;
    this.employeePendingConsentTimesheetPreviewShift = null;
  }

  viewTimesheetDetailsAsEmployee(shift) {
    if (shift.status === TimesheetStatus.pendingConsent) {
      this.openEmployeePendingConsentTimesheetPreview(shift);
    } else {
      this.openTimesheetPreview(shift);
    }
  }

  viewTimesheetDetailsAsManager(shift) {
    // TODO: open different view when pending consent
    this.openTimesheetPreview(shift);
  }

  async viewTimesheetDetails(event) {
    if (this.AUTOMATED_BREAKS_ENABLED) {
      if (this.Authentication.user.isManager) {
        // If user is manager but it's actually the other manager that proposed changes we still show consent modal
        if (
          event.status === TimesheetStatus.pendingConsent &&
          event.user.id === this.Authentication.user.id
        ) {
          this.openEmployeePendingConsentTimesheetPreview(event);
        } else {
          this.viewTimesheetDetailsAsManager(event);
        }
      } else {
        this.viewTimesheetDetailsAsEmployee(event);
      }
    } else {
      await this.viewTimesheetManagerMode(event);
    }
  }

  onCommentSave(comment) {
    this.timesheetPreviewShift = {
      ...this.timesheetPreviewShift,
      assigneeNotes: comment,
    };
    this._updateTimesheetOnList(this.timesheetPreviewShift);
  }

  async onTimesheetSave(shiftId, userId, ignoreGlobalErrorHandler = false) {
    const event = await this.EventCollection.getTimesheetEvent(shiftId, userId, {
      ignoreGlobalErrorHandler,
    });
    const extendedEvent = this.TimesheetsService.extendTimesheetModel(event);
    this._updateTimesheetOnList(extendedEvent);
  }

  async editTimesheet(event) {
    if (this.AUTOMATED_BREAKS_ENABLED) {
      this.isTimesheetEditActive = true;
      this.timesheetEditShift = event;
    } else {
      await this.viewTimesheet(event, 'edit');
    }
  }

  closeTimesheetEdit() {
    this.isTimesheetEditActive = false;
    this.timesheetEditShift = null;
  }

  async onEditTimesheet(shiftId, userId) {
    try {
      await this.onTimesheetSave(shiftId, userId);
      this.closeTimesheetEdit();
    } catch {
      //
    }
  }

  async onApproveConsent(shiftId) {
    await this.onTimesheetSave(shiftId);
    this.closeEmployeePendingConsentTimesheetPreview();
  }

  async onDenyConsent(shiftId) {
    try {
      await this.onTimesheetSave(shiftId, true);
    } catch (e) {
      /**
       * We need to check for 404 here because of edge cases with consent flow.
       * When a manager creates a timesheet for an employee and then the employee denies those changes
       * we will end up with `onTimesheetSave` actually returning 404 when the event is refetched,
       * as it basically is deleted now. We catch that case and handle it without extra
       * toast errors, making sure we also refresh the timesheet table.
       */
      if (e.status === 404) {
        const idx = this.data.findIndex(({ id }) => id === shiftId);
        if (idx >= 0) {
          delete this.data[idx];
          this.refreshItems();
          this.getTotals();
        }
      } else if (e.data) {
        const message = this.Utils.getErrorMessages(e);
        if (message?.toString().length) {
          this.AlertService.add(message, e.status);
        }
      }
    } finally {
      this.closeEmployeePendingConsentTimesheetPreview();
    }
  }

  isPendingConsent(timesheetStatus) {
    return timesheetStatus === TimesheetStatus.pendingConsent;
  }
}

export default {
  bindings: {
    saveColumns: '&',
    reportColumns: '<',
    data: '<',
    schema: '<',
    showDurationsAsDecimals: '<?',
    getDataForExport: '=?',
    sortMethod: '=?orderBy',
    selectedRows: '=',
    canDataColumnBeVisible: '<',
    type: '<',
    filtersOpened: '<',
    collapsedGroups: '=',
    toggleCollapse: '=',
  },
  templateUrl: timesheetsTableComponentUrl,
  controllerAs: 'ctrl',
  controller: TimesheetsTableCtrl,
};
